/**
 * SupervisorPage — Métriques Action Engine (US-145)
 * Agrégation BACKEND_FALLBACK + guardrail_check sur 24h glissantes.
 * Composants DS : MiniStats + BarChart (Bootstrap + theme.css uniquement).
 * Actualisation manuelle uniquement — bouton Rafraîchir (CA-4).
 * Zéro PHI — compteurs anonymisés.
 */

import { useState } from 'react';
import { apiClient } from '@/shared/services/apiClient';
import { API_ENDPOINTS } from '@/config/api';
import { MiniStats } from '@/shared/ds/charts/MiniStats/MiniStats';
import { BarChart } from '@/shared/ds/charts/BarChart/BarChart';

interface BackendDistribution {
  api_fhir: number;
  scripts: number;
  cua: number;
  hitl: number;
}

interface SupervisorMetrics {
  total_routes: number;
  backend_distribution: BackendDistribution;
  fallback_rate: number;
  vision_check_rate: number;
  window_hours: number;
}

export function SupervisorPage() {
  const [metrics, setMetrics] = useState<SupervisorMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.get<SupervisorMetrics>(API_ENDPOINTS.actionEngineMetrics);
      setMetrics(r);
    } catch {
      setError('Erreur de chargement des métriques Supervisor');
    } finally {
      setLoading(false);
    }
  };

  const dist = metrics?.backend_distribution;
  const barLabels = ['API/FHIR (sauté)', 'Scripts (sauté)', 'CUA (sauté)', 'HITL'];
  const barData = dist
    ? [dist.api_fhir, dist.scripts, dist.cua, dist.hitl]
    : [0, 0, 0, 0];

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <h2 className="fw-bold mb-0">
          <i className="bi bi-activity me-2 text-primary"></i>
          Supervisor — Action Engine
        </h2>
        {metrics && (
          <span className="badge bg-secondary">
            {metrics.window_hours}h glissantes
          </span>
        )}
        <button
          className="btn btn-outline-primary btn-sm ms-auto"
          onClick={load}
          disabled={loading}
        >
          {loading
            ? <span className="spinner-border spinner-border-sm me-1" />
            : <i className="bi bi-arrow-clockwise me-1"></i>
          }
          Rafraîchir
        </button>
      </div>

      <p className="text-muted mb-4">
        Métriques Action Engine sur fenêtre glissante 24h.
        Données anonymisées — zéro données patient (PHI).
      </p>

      {/* Erreur */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-circle"></i>
          {error}
        </div>
      )}

      {/* État initial */}
      {!metrics && !loading && !error && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-activity" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
          <p className="mt-3">Cliquez sur <strong>Rafraîchir</strong> pour charger les métriques</p>
        </div>
      )}

      {/* Métriques */}
      {metrics && (
        <>
          {/* KPIs — MiniStats DS */}
          <div className="row g-3 mb-4">
            <div className="col-sm-6 col-lg-3">
              <MiniStats
                label="Routes traitées (24h)"
                value={metrics.total_routes}
                icon="bi-diagram-3"
                variant="primary"
              />
            </div>
            <div className="col-sm-6 col-lg-3">
              <MiniStats
                label="Taux de fallback"
                value={`${(metrics.fallback_rate * 100).toFixed(1)}%`}
                icon="bi-arrow-repeat"
                variant={metrics.fallback_rate > 0.5 ? 'action' : 'neutral'}
              />
            </div>
            <div className="col-sm-6 col-lg-3">
              <MiniStats
                label="Taux de vérif. Vision"
                value={`${(metrics.vision_check_rate * 100).toFixed(1)}%`}
                icon="bi-eye"
                variant="primary"
              />
            </div>
            <div className="col-sm-6 col-lg-3">
              <MiniStats
                label="HITL déclenchés"
                value={metrics.backend_distribution.hitl}
                icon="bi-person-check"
                variant={metrics.backend_distribution.hitl > 0 ? 'action' : 'neutral'}
              />
            </div>
          </div>

          {/* Distribution backends — BarChart DS */}
          <div className="card">
            <div className="card-body">
              <BarChart
                title="Distribution backends Action Engine (fallbacks + HITL)"
                labels={barLabels}
                datasets={[
                  {
                    label: 'Événements',
                    data: barData,
                    color: '#9333ea',
                  },
                ]}
                height={220}
              />
              <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                <i className="bi bi-info-circle me-1"></i>
                "Sauté" = backend non disponible lors du routage.
                HITL = aucun backend disponible, validation humaine requise.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
